import { baseApi } from './baseApi';
import type { UserDetailsListItem } from '../../model';
import type { ProgramDetailsInterface } from '../../model/exportTypeUtil';
import type { UserProfileDetails } from '../../pages/AddChatPage';

export interface CurrentUser {
	id: number;
	email: string;
	[key: string]: unknown;
}

export interface UserDirectoryFilter {
	filter: {
		firstName: string;
		lastName: string;
		email: string;
	};
}

interface ApiEnvelope<T> {
	data: T;
}

export const userApi = baseApi.injectEndpoints({
	endpoints: (build) => ({
		getCurrentUser: build.query<CurrentUser, void>({
			query: () => '/api/rails/api/v1/users/current',
			providesTags: ['User'],
		}),
		getUserDetailsList: build.query<UserDetailsListItem[], number[]>({
			query: (userIds) => ({
				url: '/api/rails/api/v2/my/users/details_list',
				method: 'POST',
				body: { user_ids: userIds },
			}),
			transformResponse: (response: ApiEnvelope<UserDetailsListItem[]>) => response.data ?? [],
			providesTags: (_users, _error, userIds) => userIds.map((userId) => ({ type: 'Users' as const, id: userId })),
		}),
		getUserInfo: build.query<unknown, { userId: number }>({
			query: ({ userId }) => `/api/rails/api/v2/my/users/${userId}`,
			providesTags: (_user, _error, { userId }) => [{ type: 'Users', id: userId }],
		}),
		getProgramDetails: build.query<ProgramDetailsInterface, number>({
			query: (programId) => `/api/programs/${programId}`,
			transformResponse: (response: ApiEnvelope<ProgramDetailsInterface>) => response.data,
			providesTags: (_program, _error, programId) => [{ type: 'Program', id: programId }],
		}),
		getDirectoryUsers: build.query<UserProfileDetails[], UserDirectoryFilter>({
			query: (body) => ({ url: '/api/user/list', method: 'POST', body }),
			transformResponse: (response: ApiEnvelope<UserProfileDetails[]>) => response.data ?? [],
			providesTags: [{ type: 'Users', id: 'DIRECTORY' }],
		}),
	}),
	overrideExisting: false,
});

export const {
	useGetCurrentUserQuery,
	useLazyGetUserDetailsListQuery,
	useLazyGetUserInfoQuery,
	useLazyGetProgramDetailsQuery,
	useLazyGetDirectoryUsersQuery,
} = userApi;
